import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { invalidateWorkspaceRoster } from '../hooks/useWorkspaceRoster';
import './TeamManagement.css';   // kept: its global .modal / .form-group rules are still in the bundle other screens see
import './Team/team.css';
import { showErrorToast, showToast } from '../utils/toast';   // Block 233
import { messageFromError } from '../utils/apiError';
import {
  Avatar, Banner, Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal,
  PageHeader, PageLayout, SearchInput, Select, SegmentedControl, Skeleton, StatusPill, useDelayedDelete, HeaderButton, Reveal,
} from '../ui';
import { personTint, roleLabel, formRole, isAdminRole } from './Team/teamPeople';
import { seatChange, seatsAfterRemoval, seatsAfterJoin, seatChangeSentence } from './Team/SeatChangeConfirm';

// Tier 1 #9 (AF-032): who has been invited and not yet joined. A new invitee has
// no account and is not a member until they accept and sign in, so without this
// the admin saw nothing happen after pressing Add.
//
// Block I: a refused list (a member who is not an admin: 403) still shows nothing, as before. Any
// other failure is now said, with a retry: it used to hide, which read as "nobody is invited".
export function PendingInvites({ refreshKey = 0 }) {
  const [invites, setInvites] = useState(null);
  const [loadError, setLoadError] = useState('');
  const load = React.useCallback(async () => {
    try {
      const res = await api('/api/team/invites');
      const data = await res.json();
      setInvites(Array.isArray(data.invites) ? data.invites : []);
      setLoadError('');
    } catch (err) {
      setInvites(null);
      // 403: not an admin, so there is nothing of theirs to show. Anything else is a failure.
      setLoadError(err?.status === 403 ? '' : messageFromError(err, 'The invitations could not be loaded.'));
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  // Decision 7: withdrawing waits behind Undo; a failed DELETE brings the row back with the reason.
  const withdraw = useDelayedDelete({
    send: (inv, { keepalive }) => api(`/api/team/invites/${inv.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (inv) => setInvites((list) => (list || []).filter((i) => i.id !== inv.id)),
    describe: (inv) => `Invitation to ${inv.email} withdrawn.`,
    failMessage: 'Could not withdraw that invitation.',
  });

  async function resend(inv) {
    try {
      await api('/api/team/invite/resend', { method: 'POST', body: JSON.stringify({ inviteId: inv.id }) });
      showToast(`Invitation sent again to ${inv.email}.`, { type: 'success' });
      load();
    } catch (err) { showErrorToast(messageFromError(err, 'Could not resend that invitation.')); }
  }

  if (loadError) return <ErrorState title="Invitations did not load" message={loadError} onRetry={load} />;
  const shown = (invites || []).filter((inv) => !withdraw.isHidden(inv.id));
  if (shown.length === 0) return null;
  return (
    <div data-testid="pending-invites">
      <Card color="pastel-yellow" label="Invited, not joined yet" meta={`${shown.length}`} lift={false}>
        <ul className="tm-invites">
          {shown.map((inv) => (
            <li key={inv.id}>
              <span className="tm-invites__text">
                <span className="tm-invites__email">{inv.email}</span>
                <span className="td-line__sub">
                  {inv.expired ? 'Invitation expired, resend it'
                    : inv.accepted ? 'Accepted, joins when they sign in'
                    : 'Waiting for them to accept'}
                </span>
              </span>
              <Button size="sm" variant="tertiary" onClick={() => resend(inv)}>Resend</Button>
              <Button size="sm" variant="destructive" onClick={() => withdraw.remove(inv)}>Withdraw</Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function generateUserColor() {
  const hues = [220, 160, 280, 30, 0, 200, 120, 310];
  return `hsl(${hues[Math.floor(Math.random() * hues.length)]}, 60%, 55%)`;
}

const DEPARTMENTS = [
  '', 'Admin', 'Design', 'Engineering', 'Finance', 'HR',
  'IT', 'Legal', 'Management', 'Marketing', 'Operations',
  'PR', 'Product', 'Sales', 'Other',
];

// Department colours are the owner's own data (saved on this device, teamDeptColors), shown as a
// small swatch beside the name, never as a fill under text.
const DEPT_COLORS = {
  Admin: '#6b7280', Design: '#8b5cf6', Engineering: '#3b82f6',
  Finance: '#10b981', HR: '#f59e0b', IT: '#0ea5e9',
  Legal: '#64748b', Management: '#0c2d48', Marketing: '#ec4899',
  Operations: '#f97316', PR: '#a855f7', Product: '#14b8a6',
  Sales: '#22c55e', Other: '#9ca3af',
};

// Decision 35: an ordinary member is a 'member' (it was 'user').
const EMPTY_USER = { name: '', email: '', role: 'member', color: '', department: '', jobTitle: '' };

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'finance', label: 'Finance' },
  { value: 'admin', label: 'Admin' },
];

// Shared form fields — defined OUTSIDE TeamManagement so React sees a stable component reference
function UserFormFields({ data, onChange, allKnownDepts, editing = false }) {
  // customMode = user explicitly clicked "Type your own…"
  // Initialise true only when the dept value isn't in the known list (e.g. old data)
  const isUnknownDept = !!(data.department && !allKnownDepts.includes(data.department));
  const [customMode, setCustomMode] = React.useState(isUnknownDept);

  return (
    <>
      <div className="tm-form__pair">
        <Field label="Full name" required>
          <Input type="text" value={data.name} onChange={e => onChange({ ...data, name: e.target.value })} autoComplete="off" />
        </Field>
        <Field label="Email" required hint={editing ? 'The email is their sign-in, so it is changed by them.' : undefined}>
          <Input type="email" value={data.email} onChange={e => onChange({ ...data, email: e.target.value })} disabled={editing} autoComplete="off" />
        </Field>
      </div>

      <div className="tm-form__pair">
        <Field label="Department">
          <Select
            value={customMode ? '__custom__' : (data.department || '')}
            onChange={e => {
              if (e.target.value === '__custom__') {
                setCustomMode(true);
                onChange({ ...data, department: '' });
              } else {
                setCustomMode(false);
                onChange({ ...data, department: e.target.value });
              }
            }}
          >
            {allKnownDepts.map(d => <option key={d} value={d}>{d || 'None'}</option>)}
            <option value="__custom__">Type your own…</option>
          </Select>
        </Field>
        <Field label="Job title">
          <Input type="text" value={data.jobTitle || ''} onChange={e => onChange({ ...data, jobTitle: e.target.value })} placeholder="For example, Senior developer" />
        </Field>
      </div>
      {customMode && (
        <Field label="Department name">
          <Input type="text" value={data.department || ''} onChange={e => onChange({ ...data, department: e.target.value })} />
        </Field>
      )}

      <SegmentedControl label="Role" options={ROLE_OPTIONS} value={formRole(data.role)} onChange={(role) => onChange({ ...data, role })} />
    </>
  );
}

const TeamManagement = ({ embedded = false, onViewTasks } = {}) => {
  const { currentUser, isAdmin } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  // Block I: a failed load is said as one. Both used to be swallowed: the member list then read
  // "No team members match """, and the open-task counts silently became zero.
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState('');
  const [tasksError, setTasksError] = useState('');
  // Per-member open counts are only true when the server sent the whole team ('own' is the
  // caller's own tasks: a count of zero for everyone else would be invented).
  const [tasksScope, setTasksScope] = useState(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFailure, setEditFailure] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [removeBlocked, setRemoveBlocked] = useState(null);   // { id, reason }: said at the card
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [seatInfo, setSeatInfo] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Persistent custom departments
  const [customDepts, setCustomDepts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('teamCustomDepts') || '[]'); } catch { return []; }
  });
  // Custom dept colours stored separately so they survive the built-in list too
  const [deptColorOverrides, setDeptColorOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('teamDeptColors') || '{}'); } catch { return {}; }
  });

  // Add department inline form state
  const [showAddDeptForm, setShowAddDeptForm] = useState(false);
  const [newDeptName, setNewDeptName]   = useState('');
  const [newDeptColor, setNewDeptColor] = useState('');

  // Edit department state
  const [editingDept, setEditingDept] = useState(null); // { originalName, name, color }

  const PRESET_COLORS = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#f97316', '#0ea5e9', '#6b7280',
  ];

  function saveDeptColorOverride(dept, color) {
    const updated = { ...deptColorOverrides, [dept]: color };
    setDeptColorOverrides(updated);
    localStorage.setItem('teamDeptColors', JSON.stringify(updated));
  }

  function addCustomDept() {
    const d = newDeptName.trim();
    if (!d || !newDeptColor) return;
    if (!DEPARTMENTS.includes(d) && !customDepts.includes(d)) {
      const updated = [...customDepts, d];
      setCustomDepts(updated);
      localStorage.setItem('teamCustomDepts', JSON.stringify(updated));
    }
    saveDeptColorOverride(d, newDeptColor);
    setNewDeptName('');
    setNewDeptColor('');
    setShowAddDeptForm(false);
  }

  function removeCustomDept(dept) {
    const updated = customDepts.filter(d => d !== dept);
    setCustomDepts(updated);
    localStorage.setItem('teamCustomDepts', JSON.stringify(updated));
    if (deptFilter === dept) setDeptFilter('');
  }

  function openEditDept(dept) {
    setEditingDept({ originalName: dept, name: dept, color: getDeptColor(dept) });
    setShowAddDeptForm(false);
  }

  // Block K: a rename moves the members too. It used to rename only the chip and the colour, so
  // the members (still in the old department on the server) left BOTH names on the page.
  const [deptMoveError, setDeptMoveError] = useState('');
  const [deptSaving, setDeptSaving] = useState(false);

  async function saveEditDept() {
    if (!editingDept || deptSaving) return;
    const { originalName, name: rawName, color } = editingDept;
    const newName = rawName.trim();
    if (!newName || !color) return;
    setDeptMoveError('');
    if (originalName !== newName) {
      setDeptSaving(true);
      const failed = [];
      for (const u of users.filter((x) => x.department === originalName)) {
        try {
          await api(`/api/team/members/${u.id}/profile`, { method: 'PATCH', body: JSON.stringify({ department: newName }) });
        } catch (err) {
          failed.push(`${u.name}: ${messageFromError(err, 'not moved')}`);
        }
      }
      setDeptSaving(false);
      if (failed.length) setDeptMoveError(`Some people are still in ${originalName}. ${failed.join('; ')}`);
    }

    // Persist colour override under new name
    const newOverrides = { ...deptColorOverrides };
    if (originalName !== newName) delete newOverrides[originalName];
    newOverrides[newName] = color;
    setDeptColorOverrides(newOverrides);
    localStorage.setItem('teamDeptColors', JSON.stringify(newOverrides));

    // Keep customDepts list consistent
    if (originalName !== newName) {
      let updatedCustom = [...customDepts];
      if (updatedCustom.includes(originalName)) {
        updatedCustom = updatedCustom.map(d => d === originalName ? newName : d);
      } else if (!DEPARTMENTS.includes(newName) && !updatedCustom.includes(newName)) {
        updatedCustom.push(newName);
      }
      setCustomDepts(updatedCustom);
      localStorage.setItem('teamCustomDepts', JSON.stringify(updatedCustom));

      await reload();
      if (deptFilter === originalName) setDeptFilter(newName);
    }

    setEditingDept(null);
  }

  // Colour resolver — overrides take priority over DEPT_COLORS defaults
  function getDeptColor(dept) {
    return deptColorOverrides[dept] || DEPT_COLORS[dept] || '#9ca3af';
  }

  const [invitesKey, setInvitesKey] = useState(0); // Tier 1 #9: refresh the pending list after an add

  async function reload() {
    // api() THROWS on a non-2xx (src/api/client.js), so a `res.ok` check after it was dead code.
    try {
      const res = await api('/api/team/members');
      const data = await res.json();
      if (!Array.isArray(data?.members)) throw new Error('The team list came back empty-handed. Please try again.');
      setUsers(data.members.map(m => ({
        ...m,
        role: m.teamRole || m.role,
      })));
      setMembersError('');
    } catch (err) {
      setMembersError(messageFromError(err, 'Your team could not be loaded.'));
    } finally {
      setMembersLoading(false);
    }

    // Per-member open-task counts need a server aggregate — the client only holds
    // the current user's tasks. Use the business-scoped team endpoint (Block 2).
    try {
      const res = await api('/api/tasks?team=true');
      const data = await res.json();
      setTeamTasks(Array.isArray(data?.tasks) ? data.tasks : []);
      setTasksScope(data?.scope === 'team' ? 'team' : 'own');
      setTasksError('');
    } catch (err) {
      setTeamTasks([]);
      setTasksError(messageFromError(err, 'Open task counts could not be loaded.'));
    }
  }

  async function loadSeatInfo() {
    try {
      const res = await api('/api/team/seat-info');
      setSeatInfo(await res.json());
    } catch (_) {
      // Not shown: it only prices the add form's note and the removal dialog's seat line.
      setSeatInfo(null);
    }
  }

  useEffect(() => { reload(); loadSeatInfo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allDepts = [...new Set([...users.map(u => u.department).filter(Boolean), ...customDepts])].sort();

  const filtered = users.filter(u => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) ||
      (u.jobTitle || '').toLowerCase().includes(q) || (u.department || '').toLowerCase().includes(q);
    const matchesDept = !deptFilter || u.department === deptFilter;
    return matchesSearch && matchesDept;
  });

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUser.name.trim() || !newUser.email.trim()) return;
    submitAddMember(newUser);
  };

  // Decision 3 (round 3): seats follow the member count. Adding a member buys no seat here; the
  // seat is added when they join. (A full plan used to open a "buy a seat" dialog that bought it at
  // invite time, so an invitation nobody accepted left a spare paid seat.)
  const submitAddMember = async (userData) => {
    setAddLoading(true);
    try {
      const res = await api('/api/team/members/add', {
        method: 'POST',
        body: JSON.stringify({
          ...userData,
          color: userData.color || generateUserColor(),
        }),
      });
      const data = await res.json();
      // Tier 1 #9: an existing account is INVITED — say so, rather than letting
      // the admin wonder why nobody new appeared in the list.
      if (data.invited) {
        showToast(data.inviteEmailSent === false
          ? `Invitation created for ${userData.email}, but the email could not be sent. Use Resend below.`
          : `Invitation sent to ${userData.email}. They join the team when they accept and sign in.`,
        { type: data.inviteEmailSent === false ? 'error' : 'success' });
      } else {
        // Block I: a 200 that does not say what happened is not reported as a success.
        showToast(`The server did not confirm an invitation for ${userData.email}. Check the invited list below before trying again.`, { type: 'info' });
      }
      setInvitesKey((k) => k + 1);
      invalidateWorkspaceRoster();
      await reload();
      await loadSeatInfo();
      setIsAddingUser(false);
      setNewUser(EMPTY_USER);
    } catch (err) {
      showErrorToast(messageFromError(err, 'Failed to add member'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setEditFailure('');
    try {
      await api(`/api/team/members/${editingUser.id}/profile`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editingUser.name,
          department: editingUser.department,
          jobTitle: editingUser.jobTitle,
          color: editingUser.color,
          role: formRole(editingUser.role),
        }),
      });
      await reload();
      setEditingUser(null);
    } catch (err) { setEditFailure(messageFromError(err, 'Failed to update')); }
  };

  // Block I: these two rules were alert() boxes. The SERVER enforces both (routes/team.js:
  // "Cannot remove yourself", "Cannot remove the last admin"); this just says so at the card,
  // before asking.
  const handleDeleteUser = (userId) => {
    const target = users.find(u => u.id === userId);
    if (!target) return;
    if (userId === currentUser.id) { setRemoveBlocked({ id: userId, reason: 'You cannot remove yourself.' }); return; }
    const admins = users.filter(u => isAdminRole(u.role));
    if (isAdminRole(target.role) && admins.length <= 1) {
      setRemoveBlocked({ id: userId, reason: 'This is the last admin, so they cannot be removed. Make someone else an admin first.' });
      return;
    }
    setRemoveBlocked(null);
    setUserToRemove(target);
    setShowRemovalModal(true);
  };

  // The dialog waits for this; a throw shows the reason inside the dialog (ConfirmDialog).
  const handleConfirmRemoval = async () => {
    try {
      const res = await api(`/api/team/members/${userToRemove.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data?.ok) throw new Error('The server did not confirm the removal. Nothing may have changed; refresh to check.');
      showToast(`${userToRemove.name} has been removed. Their leave and HR records are kept.${data.seatSyncWarning ? ' ' + data.seatSyncWarning : ''}`, { type: 'success' });
      invalidateWorkspaceRoster();
      await reload();
      await loadSeatInfo();
    } catch (err) {
      throw new Error(messageFromError(err, 'The member could not be removed. Nothing was changed.'));
    }
  };

  function closeRemoval() { setShowRemovalModal(false); setUserToRemove(null); }

  // The seat lines (a member leaving, a member joining) are worded by Team/SeatChangeConfirm, priced
  // from seat-info's Stripe price.
  const lang = currentUser?.language || 'en-GB';
  const removalSeats = userToRemove ? seatsAfterRemoval(seatInfo) : null;
  const joinSeats = seatsAfterJoin(seatInfo);

  const countsKnown = !tasksError && tasksScope === 'team';

  const UserCard = ({ user }) => {
    const openCount = teamTasks.filter(
      t => (t.userId === user.id || (t.assignedTeam || []).includes(user.id)) && !t.completed
    ).length;
    const isYou = user.id === currentUser.id;
    const canManage = isAdmin && !isYou;

    return (
      <Card className="tm-card" color={personTint(user.id)} label={roleLabel(user.role)} meta={isYou ? 'You' : undefined}
        footer={(canManage || onViewTasks) ? (
          <>
            {onViewTasks && <Button variant="tertiary" onClick={() => onViewTasks(user.id)}>View tasks</Button>}
            {canManage && <Button variant="secondary" onClick={() => { setEditFailure(''); setEditingUser({ ...user, role: formRole(user.teamRole || user.role) }); }}>Edit</Button>}
            {canManage && <Button variant="destructive" onClick={() => handleDeleteUser(user.id)}>Remove</Button>}
          </>
        ) : undefined}>
        <div className="tm-who">
          <Avatar name={user.name} size={44} color={personTint(user.id)} />
          <div className="tm-who__text">
            <span className="tm-who__name">{user.name}</span>
            {user.jobTitle && <span className="tm-who__sub">{user.jobTitle}</span>}
            <span className="tm-who__sub">{user.email}</span>
          </div>
        </div>
        <div className="td-row-wrap">
          {user.department && (
            <StatusPill tone="neutral">
              <span className="tm-swatch" style={{ '--tm-swatch': getDeptColor(user.department) }} aria-hidden="true" /> {user.department}
            </StatusPill>
          )}
          {countsKnown && (
            <StatusPill tone={openCount > 0 ? 'pending' : 'success'}>{openCount} open task{openCount !== 1 ? 's' : ''}</StatusPill>
          )}
        </div>
        {removeBlocked?.id === user.id && <p className="td-failed" role="alert">{removeBlocked.reason}</p>}
      </Card>
    );
  };

  // Build full department list including any custom ones from existing users
  const allKnownDepts = [...new Set([...DEPARTMENTS, ...customDepts, ...users.map(u => u.department).filter(Boolean)])];

  // Group stats by department
  const deptStats = allDepts.map(dept => ({
    dept,
    count: users.filter(u => u.department === dept).length,
    color: getDeptColor(dept),
  }));

  const adminCount = users.filter(u => isAdminRole(u.role)).length;
  const subline = `${users.length} member${users.length !== 1 ? 's' : ''} · ${adminCount} admin${adminCount !== 1 ? 's' : ''}${deptStats.length > 0 ? ` · ${deptStats.length} department${deptStats.length !== 1 ? 's' : ''}` : ''}`;
  const inviteButton = isAdmin ? (
    <HeaderButton primary onClick={() => { setIsAddingUser(true); setNewUser(EMPTY_USER); }}>+ Add team member</HeaderButton>
  ) : null;

  const deptEditor = (showAddDeptForm || editingDept) && isAdmin && (() => {
    const isEditing = !!editingDept;
    const name  = isEditing ? editingDept.name  : newDeptName;
    const color = isEditing ? editingDept.color : newDeptColor;
    const setName  = v => isEditing ? setEditingDept(d => ({ ...d, name: v }))  : setNewDeptName(v);
    const setColor = v => isEditing ? setEditingDept(d => ({ ...d, color: v })) : setNewDeptColor(v);
    const onSave   = isEditing ? saveEditDept : addCustomDept;
    const onCancel = isEditing
      ? () => setEditingDept(null)
      : () => { setShowAddDeptForm(false); setNewDeptName(''); setNewDeptColor(''); };
    const canSave  = name.trim() && color;

    return (
      <Card className="tm-dept-card" color="pastel-lilac" label={isEditing ? `Edit ${editingDept.originalName}` : 'Add a department'} lift={false}
        footer={<>
          {isEditing && customDepts.includes(editingDept.originalName) && (
            <Button variant="destructive" onClick={() => { removeCustomDept(editingDept.originalName); setEditingDept(null); }}>Delete</Button>
          )}
          <Button variant="tertiary" onClick={onCancel}>Cancel</Button>
          <Button variant="confirm" onClick={onSave} disabled={!canSave || deptSaving} aria-busy={deptSaving || undefined}>{deptSaving ? 'Moving people…' : isEditing ? 'Save changes' : 'Add department'}</Button>
        </>}>
        <Reveal key={isEditing ? `edit-${editingDept.originalName}` : "add"} className="tm-dept-form">
          <Field label="Department name">
            <Input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && canSave && onSave()} />
          </Field>
          <div className="tm-swatches" role="group" aria-label="Colour">
            {PRESET_COLORS.map(c => (
              <button key={c} type="button" className="tm-swatch-btn" aria-pressed={color === c} aria-label={`Colour ${c}`} onClick={() => setColor(c)}>
                <span style={{ '--tm-swatch': c }} />
              </button>
            ))}
            <label className="tm-swatch-btn" aria-label="Choose your own colour">
              <input type="color" value={!PRESET_COLORS.includes(color) && color ? color : '#9ca3af'} onChange={e => setColor(e.target.value)} />
            </label>
          </div>
          <p className="td-muted">Departments and their colours are kept on this device for now.</p>
        </Reveal>
      </Card>
    );
  })();

  const body = (
    <div className="td-stack">
      {embedded && <p className="td-muted">{subline}</p>}

      {/* Department filter */}
      <div className="tm-depts" role="group" aria-label="Filter by department">
        <Chip selected={!deptFilter} onClick={() => setDeptFilter('')}>All · {users.length}</Chip>
        {[...deptStats, ...customDepts.filter(d => !deptStats.find(s => s.dept === d)).map(dept => ({ dept, count: 0, color: getDeptColor(dept), customOnly: true }))].map(({ dept, count, color }) => (
          <span key={dept} className="tm-dept">
            <Chip selected={deptFilter === dept} onClick={() => setDeptFilter(deptFilter === dept ? '' : dept)}>
              <span className="tm-swatch" style={{ '--tm-swatch': color }} aria-hidden="true" />
              {dept} · {count}
            </Chip>
            {isAdmin && (
              <button type="button" className="tm-icon-btn" aria-label={`Edit the ${dept} department`} onClick={() => openEditDept(dept)}>✐</button>
            )}
          </span>
        ))}
        {isAdmin && !showAddDeptForm && !editingDept && (
          <Button size="sm" variant="tertiary" onClick={() => setShowAddDeptForm(true)}>+ Department</Button>
        )}
      </div>

      {deptEditor}
      {deptMoveError && <Banner tone="problem" onDismiss={() => setDeptMoveError('')}>{deptMoveError}</Banner>}

      {/* Controls */}
      <div className="tm-toolbar">
        <SearchInput label="Search the team" placeholder="Search by name, email, department or job title" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        {embedded && inviteButton}
      </div>

      <PendingInvites refreshKey={invitesKey} />

      {tasksError && !membersError && (
        <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={() => reload()}>Try again</Button>}>
          Open task counts could not be loaded: {tasksError}
        </Banner>
      )}

      {/* Users Grid */}
      {membersLoading ? (
        <Skeleton lines={4} label="Loading your team" />
      ) : membersError ? (
        <ErrorState title="Your team did not load" message={membersError} onRetry={() => { setMembersLoading(true); reload(); }} />
      ) : filtered.length === 0 ? (
        <EmptyState title={users.length === 0 ? 'No one here yet' : 'No one matches'}
          action={(searchQuery || deptFilter)
            ? <Button variant="tertiary" onClick={() => { setSearchQuery(''); setDeptFilter(''); }}>Clear the search</Button>
            : inviteButton}>
          {users.length === 0 ? 'Add a team member and they appear here once they join.' : `No team members match "${searchQuery || deptFilter}".`}
        </EmptyState>
      ) : (
        <div className="tm-grid">
          {filtered.map(user => <UserCard key={user.id} user={user} />)}
        </div>
      )}

      {/* Department overview */}
      {!membersError && deptStats.length > 0 && (
        <section className="td-stack" aria-labelledby="tm-depts-title">
          <h2 id="tm-depts-title" className="tm-section-title">Departments</h2>
          <div className="tm-dept-grid">
            {deptStats.map(({ dept, count, color }) => (
              <Card key={dept} lift={false} label={<><span className="tm-swatch" style={{ '--tm-swatch': color }} aria-hidden="true" /> {dept}</>} meta={`${count} member${count !== 1 ? 's' : ''}`}>
                <p className="td-muted">{users.filter(u => u.department === dept).map(u => u.name).join(', ')}</p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Add Member Modal */}
      <Modal open={isAddingUser} onClose={() => (addLoading ? undefined : setIsAddingUser(false))} title="Add a team member" color="pastel-lilac" width={560}
        footer={<>
          <Button variant="tertiary" onClick={() => setIsAddingUser(false)} disabled={addLoading}>Cancel</Button>
          <Button variant="confirm" type="submit" form="tm-add-form" disabled={addLoading} aria-busy={addLoading || undefined}>{addLoading ? 'Adding…' : 'Send invitation'}</Button>
        </>}>
        <form id="tm-add-form" onSubmit={handleAddUser} className="tm-form">
          <p className="td-muted">They get an email invitation, and join the team when they accept and sign in.</p>
          {joinSeats != null && (
            <p className="td-muted" data-testid="seat-join-note">
              {seatChangeSentence(seatChange(seatInfo, joinSeats), lang).replace(/^Your seats/, 'When they join, your seats')}
            </p>
          )}
          <UserFormFields data={newUser} onChange={setNewUser} allKnownDepts={allKnownDepts} />
        </form>
      </Modal>

      {/* Edit Member Modal */}
      <Modal open={!!editingUser} onClose={() => setEditingUser(null)} title={editingUser ? `Edit ${editingUser.name}` : ''} color="pastel-lilac" width={560}
        footer={<>
          <Button variant="tertiary" onClick={() => setEditingUser(null)}>Cancel</Button>
          <Button variant="confirm" type="submit" form="tm-edit-form">Save changes</Button>
        </>}>
        {editingUser && (
          <form id="tm-edit-form" onSubmit={handleUpdateUser} className="tm-form">
            <UserFormFields data={editingUser} onChange={setEditingUser} allKnownDepts={allKnownDepts} editing />
            {editFailure && <p className="td-failed" role="alert">{editFailure}</p>}
          </form>
        )}
      </Modal>

      {/* Remove Confirmation */}
      <ConfirmDialog
        open={showRemovalModal && !!userToRemove}
        onClose={closeRemoval}
        onConfirm={handleConfirmRemoval}
        title={userToRemove ? `Remove ${userToRemove.name}?` : 'Remove?'}
        confirmLabel="Remove"
        message={userToRemove ? `${userToRemove.name} will lose access to the team immediately and be signed out. Their leave and HR records are kept. Their own Aidey account keeps working for 14 days so they can move to a plan of their own. Their tasks will remain unassigned.${removalSeats != null ? ` ${seatChangeSentence(seatChange(seatInfo, removalSeats), lang)}` : ''}` : ''}
      />
    </div>
  );

  if (embedded) return body;
  return (
    <PageLayout>
      <PageHeader eyebrow={{ label: 'Team', color: 'group-team' }} title="Team management" subline={subline} primary={inviteButton} />
      {body}
    </PageLayout>
  );
};

export default TeamManagement;
